import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { supabase } from "../../lib/supabase";

const LikeButton = ({
  postId,
  currentUser,
  onLikeChange,
  onCountChange,
  initialCount,
  initialLiked,
  prefetched = false,
}) => {
  const [isLiked, setIsLiked] = useState(Boolean(initialLiked));
  const [likeCount, setLikeCount] = useState(
    typeof initialCount === "number" ? initialCount : 0
  );
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000";

  const formatCount = (n) =>
    new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n || 0);

  useEffect(() => {
    if (typeof initialCount === "number") {
      setLikeCount(initialCount);
    }
    if (typeof initialLiked === "boolean") {
      setIsLiked(initialLiked);
    }
  }, [initialCount, initialLiked]);

  useEffect(() => {
    if (!prefetched) {
      fetchLikes();
    }
  }, [postId, currentUser, prefetched]);

  const fetchLikes = async () => {
    try {
      // Optional auth token
      let headers = {};
      if (currentUser) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token =
          session?.access_token || localStorage.getItem("access_token");
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }

      const response = await fetch(
        `${baseUrl}/api/gallery/posts/like/${postId}`,
        { headers }
      );
      const result = await response.json();

      if (result.success) {
        // Only update if not currently processing a like action
        if (!isLoading) {
          setLikeCount(result.likeCount || 0);
          setIsLiked(result.userHasLiked || false);
        }
        if (typeof onCountChange === "function") {
          onCountChange(result.likeCount || 0);
        }
      }
    } catch (error) {
      console.error("Error fetching likes:", error);
    }
  };

  const handleLike = async () => {
    if (!currentUser) {
      toast.error("Please sign in to like posts");
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    const previousLiked = isLiked;
    const previousCount = likeCount;

    // Optimistic update
    const optimisticLiked = !isLiked;
    const optimisticCount = isLiked ? likeCount - 1 : likeCount + 1;
    setIsLiked(optimisticLiked);
    setLikeCount(optimisticCount);

    try {
      // Get token from Supabase session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token =
        session?.access_token || localStorage.getItem("access_token");

      const response = await fetch(
        `${baseUrl}/api/gallery/post/like/${postId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        // Trust the backend response and update accordingly
        const newIsLiked = result.action === "liked";
        setIsLiked(newIsLiked);

        // Calculate final count based on the actual action
        if (newIsLiked && !previousLiked) {
          // Was not liked, now liked: +1
          setLikeCount(previousCount + 1);
        } else if (!newIsLiked && previousLiked) {
          // Was liked, now not liked: -1
          setLikeCount(previousCount - 1);
        }
        // If no change in like state, keep count as is

        // Notify parent
        if (onLikeChange) {
          onLikeChange();
        }
      } else {
        // Revert on failure
        setIsLiked(previousLiked);
        setLikeCount(previousCount);
        throw new Error(result.message || "Failed to update like");
      }
    } catch (error) {
      console.error("Error updating like:", error);
      toast.error("Failed to update like. Please try again.");

      // Revert optimistic update
      setIsLiked(previousLiked);
      setLikeCount(previousCount);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleLike}
      disabled={isLoading}
      aria-pressed={isLiked}
      aria-busy={isLoading}
      aria-label={isLiked ? "Unlike post" : "Like post"}
      title={isLiked ? "Unlike" : "Like"}
      className={`inline-flex items-center gap-2 h-9 sm:h-10 px-3 sm:px-3 rounded-2xl motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-jacarta-800 disabled:opacity-60 disabled:cursor-not-allowed ${
        isLiked
          ? "text-red-500 bg-red-50 dark:bg-red-900/20"
          : "text-jacarta-600 hover:text-red-500 hover:bg-red-50 dark:text-jacarta-400 dark:hover:bg-red-900/20"
      }`}
    >
      <svg
        className={`w-5 h-5 shrink-0 block ${isLoading ? "animate-pulse" : ""}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.7 4C18.87 4 21 6.98 21 9.76C21 15.39 12.16 20 12 20C11.84 20 3 15.39 3 9.76C3 6.98 5.13 4 8.3 4C10.12 4 11.31 4.91 12 5.71C12.69 4.91 13.88 4 15.7 4Z"
          fill={isLiked ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="font-display font-medium leading-none tabular-nums text-[12px] sm:text-sm">
        {formatCount(likeCount)}
      </span>
    </button>
  );
};

export default LikeButton;
